import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const DriverCalendar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthSchedule, setMonthSchedule] = useState([]);
  const [dayEvents, setDayEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverTimezone, setDriverTimezone] = useState(null);

  // Get date/time components of a UTC ISO string in a specific timezone
  const getPartsInTZ = (isoString, timezone) => {
    if (!timezone) {
      // Fallback to browser timezone
      const d = new Date(isoString);
      return {
        dateString: d.toDateString(),
        hours: d.getHours(),
        minutes: d.getMinutes(),
        totalMinutes: d.getHours() * 60 + d.getMinutes()
      };
    }
    const date = new Date(isoString);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);
    const get = (type) => parts.find(p => p.type === type)?.value;
    const hour = parseInt(get('hour'));
    const minute = parseInt(get('minute'));
    return {
      dateString: new Date(parseInt(get('year')), parseInt(get('month')) - 1, parseInt(get('day'))).toDateString(),
      hours: hour,
      minutes: minute,
      totalMinutes: hour * 60 + minute
    };
  };

  useEffect(() => {
    // Check if date param exists in URL
    const dateParam = searchParams.get('date');
    if (dateParam) {
      // Parse date string as local date, not UTC
      const [year, month, day] = dateParam.split('-').map(Number);
      const localDate = new Date(year, month - 1, day); // month is 0-indexed
      setSelectedDate(localDate);
      setCurrentMonth(localDate);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchMonthSchedule();
  }, [currentMonth]);

  useEffect(() => {
    updateDayEvents();
  }, [selectedDate, monthSchedule]);

    const fetchMonthSchedule = async () => {
    try {
      setLoading(true);
      const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      // Send date-only strings (YYYY-MM-DD) so the backend treats them as driver-local dates
      const pad = (n) => String(n).padStart(2, '0');
      const startDateStr = `${firstDay.getFullYear()}-${pad(firstDay.getMonth() + 1)}-${pad(firstDay.getDate())}`;
      const endDateStr = `${lastDay.getFullYear()}-${pad(lastDay.getMonth() + 1)}-${pad(lastDay.getDate())}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/rides/calendar`,
        {
          params: {
            start_date: startDateStr,
            end_date: endDateStr
          }
        }
      );

      // Handle both old (array) and new (object with events + driver_timezone) response shapes
      const responseData = response.data;
      const events = Array.isArray(responseData) ? responseData : (responseData.events || []);
      const tz = responseData.driver_timezone || null;
      setDriverTimezone(tz);

      // Group events by date in the driver's timezone
      const eventsByDate = {};

      events.forEach(event => {
        const eventDate = getPartsInTZ(event.start, tz).dateString;
        if (!eventsByDate[eventDate]) {
          eventsByDate[eventDate] = {
            hasRides: false,
            hasAvailability: false,
            hasBlackout: false,
            hasDayBookings: false,
            events: []
          };
        }

        eventsByDate[eventDate].events.push(event);

        // Track event types for markers
        if (event.type === 'ride') {
          eventsByDate[eventDate].hasRides = true;
        } else if (event.type === 'available') {
          eventsByDate[eventDate].hasAvailability = true;
        } else if (event.type === 'blackout') {
          eventsByDate[eventDate].hasBlackout = true;
        } else if (event.type === 'day-booking') {
          eventsByDate[eventDate].hasDayBookings = true;
        }
      });

      setMonthSchedule(eventsByDate);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching month schedule:', error);
      setMonthSchedule({});
      setLoading(false);
    }
  };

  const updateDayEvents = () => {
    // Use toDateString() to match the key format in monthSchedule
    const dateStr = selectedDate.toDateString();
    
    const dayData = monthSchedule[dateStr];
    setDayEvents(dayData?.events || []);
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getDayStatus = (date) => {
    if (!date) return null;
    
    // Use toDateString() to match the key format in monthSchedule
    const dateStr = date.toDateString();
    const dayData = monthSchedule[dateStr];
    
    if (!dayData) return null;
    
    // Build summary from the day's events
    return {
      total_rides: dayData.events.filter(e => e.type === 'ride').length,
      has_blackout: dayData.hasBlackout
    };
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const changeMonth = (offset) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setCurrentMonth(newMonth);
  };

  const formatTime = (timeStr) => {
    const [hour, minute] = timeStr.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${minute} ${ampm}`;
  };

  const formatEventTime = (isoString) => {
    const { hours, minutes } = getPartsInTZ(isoString, driverTimezone);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  const getEventColor = (type) => {
    const colors = {
      'ride': 'bg-green-400 border-green-500',
      'pre-buffer': 'bg-yellow-300 border-yellow-400',
      'post-buffer': 'bg-yellow-300 border-yellow-400',
      'blackout': 'bg-purple-500 border-purple-600',
      'available': 'bg-blue-300 border-blue-400',
      'day-booking': 'bg-orange-400 border-orange-500',
      'day-booking-pre-buffer': 'bg-orange-300 border-orange-400',
      'day-booking-post-buffer': 'bg-orange-300 border-orange-400'
    };
    return colors[type] || 'bg-gray-300 border-gray-400';
  };

  const getEventTextColor = (type) => {
    const colors = {
      'ride': 'text-green-900',
      'pre-buffer': 'text-yellow-900',
      'post-buffer': 'text-yellow-900',
      'blackout': 'text-white',
      'available': 'text-blue-900',
      'day-booking': 'text-orange-900',
      'day-booking-pre-buffer': 'text-orange-900',
      'day-booking-post-buffer': 'text-orange-900'
    };
    return colors[type] || 'text-gray-900';
  };

  const getEventZIndex = (type) => {
    // Higher z-index = on top
    const zIndexes = {
      'available': 'z-0',      // Bottom layer
      'pre-buffer': 'z-20',     // On top of available
      'ride': 'z-20',           // On top of available
      'post-buffer': 'z-20',    // On top of available
      'day-booking-pre-buffer': 'z-20',  // Same as regular buffers
      'day-booking': 'z-20',    // Same as rides
      'day-booking-post-buffer': 'z-20', // Same as regular buffers
      'blackout': 'z-30'        // Top layer - covers everything
    };
    return zIndexes[type] || 'z-0';
  };

  // Convert time string to minutes since midnight
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calculate position and height for an event
  const getEventStyle = (event) => {
    // Extract time in the driver's timezone
    const startParts = getPartsInTZ(event.start, driverTimezone);
    const endParts = getPartsInTZ(event.end, driverTimezone);

    const startMinutes = startParts.totalMinutes;
    let endMinutes = endParts.totalMinutes;
    // Handle events spanning midnight (end is on the next day)
    if (endMinutes <= startMinutes) {
      endMinutes = 24 * 60 - 1; // Cap at end of day
    }
    const durationMinutes = endMinutes - startMinutes;

    // Each hour is 60px tall
    const pixelsPerMinute = 1; // 60px per 60 minutes

    const top = startMinutes * pixelsPerMinute;
    const height = durationMinutes * pixelsPerMinute;

    return {
      top: `${top}px`,
      height: `${height}px`
    };
  };

  // Generate hour markers for timeline
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const days = getDaysInMonth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">My Schedule</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Left Panel - Month Calendar */}
          <div className="lg:col-span-4 bg-white rounded-lg shadow p-4 sm:p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                ←
              </button>
              <h2 className="text-base sm:text-lg font-bold">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                →
              </button>
            </div>

            {/* Quick Actions */}
            <div className="mb-4">
              <button
                onClick={() => {
                  const today = new Date();
                  setSelectedDate(today);
                  setCurrentMonth(today);
                }}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm"
              >
                Today
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="mb-4">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                  <div key={idx} className="text-center text-[10px] sm:text-xs font-medium text-gray-600 py-1">
                    <span className="sm:hidden">{day}</span>
                    <span className="hidden sm:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][idx]}</span>
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                {days.map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }

                  const status = getDayStatus(date);
                  const hasRides = status?.total_rides > 0;
                  const hasBlackout = status?.has_blackout;
                  const dateStr = date.toDateString();
                  const hasDayBookings = monthSchedule[dateStr]?.hasDayBookings;

                  return (
                    <button
                      key={date.toISOString()}
                      onClick={() => setSelectedDate(date)}
                      className={`
                        aspect-square p-0.5 sm:p-1 rounded text-xs sm:text-sm relative
                        ${isSelected(date) ? 'bg-blue-600 text-white font-bold' : ''}
                        ${isToday(date) && !isSelected(date) ? 'border-2 border-blue-600 font-bold' : ''}
                        ${!isSelected(date) && !isToday(date) ? 'hover:bg-gray-100' : ''}
                      `}
                    >
                      <div>{date.getDate()}</div>
                      {hasDayBookings && !isSelected(date) && (
                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-500 rounded-full" />
                      )}
                      {hasRides && !hasDayBookings && !isSelected(date) && (
                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-green-500 rounded-full" />
                      )}
                      {hasBlackout && !hasRides && !hasDayBookings && !isSelected(date) && (
                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-purple-500 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="border-t pt-3 sm:pt-4 grid grid-cols-2 sm:grid-cols-1 gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-400 rounded flex-shrink-0"></div>
                <span>Scheduled Rides</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-orange-400 rounded flex-shrink-0"></div>
                <span>Day Bookings</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-yellow-300 rounded flex-shrink-0"></div>
                <span>Ride Buffers</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-orange-300 rounded flex-shrink-0"></div>
                <span>Day Booking Buffers</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-purple-500 rounded flex-shrink-0"></div>
                <span>Blackout Period</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-300 rounded flex-shrink-0"></div>
                <span>Available Time</span>
              </div>
            </div>
          </div>

          {/* Right Panel - Day Timeline */}
          <div className="lg:col-span-8 bg-white rounded-lg shadow">
            {/* Day Header */}
            <div className="border-b p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold">
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h2>
              {dayEvents.length === 0 && (
                <p className="text-gray-500 mt-2">No events scheduled for this day</p>
              )}
            </div>

            {/* Timeline */}
            <div className="p-3 sm:p-6 max-h-[500px] sm:max-h-[600px] overflow-y-auto">
              <div className="relative">
                {/* Hour markers */}
                {hours.map(hour => {
                  const hourStr = `${String(hour).padStart(2, '0')}:00`;

                  return (
                    <div key={hour} className="flex border-b border-gray-200 h-[60px] relative">
                      {/* Time label */}
                      <div className="w-14 sm:w-20 flex-shrink-0 text-right pr-2 sm:pr-4 py-2 text-xs sm:text-sm text-gray-500">
                        {formatTime(hourStr)}
                      </div>

                      {/* Event area - this is where events will be positioned */}
                      <div className="flex-1 relative">
                        {/* Events are rendered here with absolute positioning */}
                      </div>
                    </div>
                  );
                })}

                {/* Render all events with absolute positioning */}
                <div className="absolute top-0 left-14 sm:left-20 right-0 bottom-0 pointer-events-none">
                  {dayEvents.map((event, idx) => {
                    const style = getEventStyle(event);
                    const eventHeight = parseInt(style.height);
                    
                    // Determine what to show based on event height
                    const showFullDetails = eventHeight >= 80;
                    const showMinimal = eventHeight >= 40 && eventHeight < 80;
                    const showTitleOnly = eventHeight >= 25 && eventHeight < 40;
                    const isTiny = eventHeight < 25;
                    
                    // Build tooltip only for non-available events
                    const tooltipText = event.type !== 'available' 
                      ? `${event.title}\n${formatEventTime(event.start)} - ${formatEventTime(event.end)}${event.pickup ? '\n📍 ' + event.pickup : ''}${event.dropoff ? '\n🎯 ' + event.dropoff : ''}${event.price ? '\n💰 $' + event.price.toFixed(2) : ''}`
                      : null;
                    
                    return (
                      <div
                        key={idx}
                        className={`
                          absolute left-2 right-2
                          ${getEventColor(event.type)} ${getEventTextColor(event.type)}
                          ${getEventZIndex(event.type)}
                          border-l-4 rounded shadow-sm pointer-events-auto
                          ${event.type !== 'available' ? 'cursor-pointer hover:z-50 transition-all' : ''}
                          overflow-hidden
                          ${isTiny ? 'p-1' : 'p-2'}
                        `}
                        style={style}
                        title={tooltipText}
                      >
                        {isTiny ? (
                          // Too small - just show colored block
                          <div className="text-xs truncate">{event.title.split(' - ')[0]}</div>
                        ) : showTitleOnly ? (
                          // Small - just title
                          <div className="text-xs font-semibold truncate">{event.title}</div>
                        ) : showMinimal ? (
                          // Medium - title and time
                          <>
                            <div className="text-xs font-semibold truncate">{event.title}</div>
                            <div className="text-xs truncate">
                              {formatEventTime(event.start)} - {formatEventTime(event.end)}
                            </div>
                          </>
                        ) : (
                          // Large - full details
                          <>
                            <div className="font-semibold text-sm truncate">{event.title}</div>
                            <div className="text-xs mt-1">
                              {formatEventTime(event.start)} - {formatEventTime(event.end)}
                            </div>
                            {event.pickup && (
                              <div className="text-xs mt-1 opacity-90">
                                <div className="truncate">📍 {event.pickup}</div>
                                {event.dropoff && (
                                  <div className="truncate">🎯 {event.dropoff}</div>
                                )}
                                {event.price && (
                                  <div>💰 ${event.price.toFixed(2)}</div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DriverCalendar;